import { useInfluencerStore } from '../../stores/creatorStore'

import React, { useState } from 'react'
import { Box, Typography, Grid, Chip } from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import InfoIcon from '@mui/icons-material/Info'
import ReactCountryFlag from 'react-country-flag'
import { useStrings } from '../../assets/localization/strings'

export default function CreatorProfile() {
  const { influencer, content } = useInfluencerStore()
  const strings = useStrings()

  const calculateAge = birthDate => {
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDifference = today.getMonth() - birth.getMonth()
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    return age
  }

  return (
    <Box sx={{ maxWidth: '1000px', margin: '0 auto', padding: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
        <Typography variant='h5' fontWeight='bold' color='primary'>
          {influencer.first_name} {influencer.last_name}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left side - Profile image and basic info */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              marginBottom: 3
            }}>
            {influencer.image ? (
              <img src={influencer.image} alt={`${influencer.first_name} profile`} style={{ width: '100%', borderRadius: '8px' }} />
            ) : (
              <img
                src={`https://ui-avatars.com/api/?name=${influencer.first_name?.[0] || ''}${influencer.last_name?.[0] || ''}&background=AB95FF&color=fff&size=256`}
                alt={`${influencer.first_name} profile`}
                style={{ width: '100%', borderRadius: '8px' }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 2
            }}>
            <Typography variant='h5' sx={{ mr: 1 }}>
              {influencer.first_name}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#f0e6ff',
                borderRadius: 1,
                px: 1,
                py: 0.5
              }}>
              <StarIcon sx={{ color: '#9370DB', fontSize: 18, mr: 0.5 }} />
              <Typography fontWeight='bold'>{influencer.averageRating}</Typography>
            </Box>
          </Box>

          <Typography textAlign='center' color='text.secondary' gutterBottom>
            {calculateAge(influencer.birth_date)} {strings.about_me_age}, {influencer.gender || 'not specified'}, {influencer.city}{' '}
            <ReactCountryFlag countryCode={influencer.country ?? 'AT'} />
          </Typography>
        </Grid>

        {/* Right side - About me and details */}
        <Grid item xs={12} md={8}>
          {influencer.bio && (
            <>
              <Typography variant='h6' fontWeight='bold' sx={{ mb: 2 }}>
                {strings.about_me}
              </Typography>
              <Typography paragraph variant='body1' sx={{ mb: 3, wordBreak: 'break-word', whiteSpace: 'pre-line', fontSize: '15px' }}>
                {influencer.bio}
              </Typography>
            </>
          )}

          <Box sx={{ mb: 3 }}>
            {influencer.tag_groups.map(group => (
              <Box key={group.id} sx={{ mb: 3 }}>
                <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 1 }}>
                  {strings[`tagGroup${group.name.charAt(0).toUpperCase() + group.name.slice(1).toLowerCase()}`]}
                </Typography>
                <Grid container spacing={1}>
                  {group.tags.map(tag => (
                    <Grid item key={tag.id}>
                      <Chip
                        label={strings[`tag${tag.name.charAt(0).toUpperCase() + tag.name.slice(1).toLowerCase()}`]}
                        size='medium'
                        sx={{
                          borderRadius: 1,
                          bgcolor: tag.selected ? '#E1D5FF' : '#f5f5f5',
                          color: tag.selected ? '#6A1B9A' : 'inherit',
                          mb: 1
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#E1D5FF', borderRadius: 1, p: 1, mb: 2 }}>
            <InfoIcon sx={{ color: '#6A1B9A', mr: 1 }} />
            <Typography variant='body2' color='#6A1B9A'>
              {strings.about_me_info}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Content Showcase */}
      <Box sx={{ mt: 4 }}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          {influencer.contentCV ? strings.about_me_content_examples : strings.about_me_content}
        </Typography>
        <Grid container spacing={2}>
          {influencer.contentCV &&
            influencer.contentCV.map((videoUrl, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    bgcolor: '#f5f5f5',
                    height: 500
                  }}>
                  <Box
                    component='video'
                    controls
                    preload='metadata'
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 2,
                      display: 'block'
                    }}>
                    <source src={videoUrl} type='video/mp4' />
                    {strings.about_me_error}
                  </Box>
                </Box>
              </Grid>
            ))}
        </Grid>
        <Grid container spacing={2} mt={1}>
          {!influencer.contentCV &&
            content &&
            content.map((mediaUrl, index) => {
              const isVideo = mediaUrl.includes('video') || mediaUrl.endsWith('.mp4')
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      bgcolor: '#f5f5f5',
                      height: 500
                    }}>
                    {isVideo ? (
                      <Box
                        component='video'
                        controls
                        preload='metadata'
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}>
                        <source src={mediaUrl} type='video/mp4' />
                        {strings.about_me_error}
                      </Box>
                    ) : (
                      <Box
                        component='img'
                        src={mediaUrl}
                        alt={`media-${index}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              )
            })}
        </Grid>
      </Box>
    </Box>
  )
}
